/**
 * Native Lifecycle Bridge
 *
 * Listens for app lifecycle messages from the React Native shell (via postMessage)
 * and triggers WebSocket reconnection/disconnection accordingly.
 *
 * When the React Native app goes to background, the OS may silently kill the
 * WebSocket TCP connection without delivering an `onclose` event to the WebView.
 * This bridge allows the native shell to signal foreground/background transitions
 * so the web app can proactively reconnect and recover missed messages.
 *
 * Message protocol (sent by React Native via webViewRef.postMessage):
 *   { type: 'APP_FOREGROUNDED' }   - App returned to foreground
 *   { type: 'APP_BACKGROUNDED' }   - App moved to background (optional)
 *
 * Dependencies are injected to avoid a circular dependency with the websocket index.
 */
import isLoadedFromMobile from '@app/helpers/platform';

export interface NativeLifecycleBridgeDeps {
  getWebSocketStatus: () => {
    connected: boolean;
    readyState: string | null;
    lastTimestamp?: number | null;
  };
  reconnectWebSocket: (options?: { force?: boolean }) => Promise<unknown>;
}

const LIFECYCLE_MESSAGE_TYPES = new Set([
  'APP_FOREGROUNDED',
  'APP_BACKGROUNDED',
]);

type LifecycleMessage =
  | { type: 'APP_FOREGROUNDED' }
  | { type: 'APP_BACKGROUNDED' };

function isLifecycleMessage(data: unknown): data is LifecycleMessage {
  if (typeof data !== 'object' || data === null) return false;
  const { type } = data as { type?: string };
  return typeof type === 'string' && LIFECYCLE_MESSAGE_TYPES.has(type);
}

let messageHandler: ((event: MessageEvent) => void) | null = null;
let bridgeDeps: NativeLifecycleBridgeDeps | null = null;

function handleAppForegrounded(deps: NativeLifecycleBridgeDeps): void {
  const status = deps.getWebSocketStatus();
  console.log(
    '[WEBSOCKET_LIFECYCLE] App foregrounded, current status:',
    status,
  );
  // Force reconnect so we replace any existing socket. On mobile the OS may
  // kill the TCP connection in background without firing onclose, leaving
  // readyState as OPEN; connect() would then return early and never recover.
  deps.reconnectWebSocket({ force: true }).catch((error) => {
    console.error(
      '[WEBSOCKET_LIFECYCLE] Failed to reconnect on foreground:',
      error,
    );
  });
}

function handleAppBackgrounded(): void {
  console.log('[WEBSOCKET_LIFECYCLE] App backgrounded');
  // Intentionally NOT disconnecting here. The WebSocket connection may survive
  // brief background periods, and the auto-reconnect backoff handles drops.
}

function getLifecycleHandlers(
  deps: NativeLifecycleBridgeDeps,
): Record<string, () => void> {
  return {
    APP_FOREGROUNDED: () => handleAppForegrounded(deps),
    APP_BACKGROUNDED: handleAppBackgrounded,
  };
}

function handleLifecycleMessage(
  message: LifecycleMessage,
  deps: NativeLifecycleBridgeDeps,
): void {
  const handlers = getLifecycleHandlers(deps);
  const handler = handlers[message.type];
  if (handler) handler();
}

/**
 * Initialize the native lifecycle bridge.
 * Safe to call on all platforms — the listener is only registered when the
 * app is loaded inside a mobile WebView.
 *
 * Dependencies are injected to avoid a circular dependency with the websocket
 * index and to improve testability.
 */
export function initNativeLifecycleBridge(
  deps: NativeLifecycleBridgeDeps,
): void {
  if (typeof window === 'undefined') return;
  if (!isLoadedFromMobile()) return;
  if (messageHandler) return; // Already initialized

  bridgeDeps = deps;
  messageHandler = (event: MessageEvent) => {
    if (!bridgeDeps) return;
    try {
      // React Native postMessage sends string data
      const data =
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

      if (isLifecycleMessage(data)) {
        handleLifecycleMessage(data, bridgeDeps);
      }
    } catch {
      // Not a JSON message or not a lifecycle message — ignore silently.
      // Other bridges (NativeAuthBridge, etc.) handle their own message types.
    }
  };

  window.addEventListener('message', messageHandler);
  console.log('[WEBSOCKET_LIFECYCLE] Native lifecycle bridge initialized');
}

/**
 * Tear down the native lifecycle bridge.
 * Called during app cleanup (e.g., logout).
 */
export function destroyNativeLifecycleBridge(): void {
  if (messageHandler) {
    window.removeEventListener('message', messageHandler);
    messageHandler = null;
    bridgeDeps = null;
    console.log('[WEBSOCKET_LIFECYCLE] Native lifecycle bridge destroyed');
  }
}
